import React, { useEffect, useState } from 'react';
import { Button } from '../../../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import { useNavigate } from 'react-router-dom';
import Loading from '../../../components/Loading';
import { Heading } from '../../../components/Heading';
import { useAppUser } from '../../../hooks/useAppUser';
import Pagination from '../../../components/Pagination';
import { EyeIcon } from '@heroicons/react/16/solid';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { useWarehouses } from '../../../hooks/useWarehouse';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { Text } from '../../../components/Text';

const COLUMNS = [
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'Email' },
    { key: 'warehouse', label: 'Warehouse' },
];
const SORT_OPTIONS = [
    { label: 'Name (A-Z)', value: 'az' },
    { label: 'Name (Z-A)', value: 'za' },
];

const AppUserCurrentLocationPage: React.FC = () => {
    const { loading, error, fetchAllUsers, appUsers, deleteUser } = useAppUser();
    const { warehouses } = useWarehouses();
    const [searchQuery, setSearchQuery] = useState('');
    const [filterWarehouseId, setFilterWarehouseId] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const perPage = 10;
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false); // Dialog open state
    const [isDeleting, setIsDeleting] = useState(false); // Loading state for deletion
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('az');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');

    useEffect(() => {
        document.title = "User Location Tracking";
    }, []);

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseId('');
    };

    const availableYears = Array.from(
        new Set(
            appUsers
                .map((u) => new Date(u.created_time || '').getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    const filteredUsers = appUsers.filter(user => {
        const matchesSearch = user.username.toLowerCase().includes(searchQuery.toLowerCase()) && user.is_admin != '1';
        const matchesWarehouse = filterWarehouseId ? user.warehouse_id == filterWarehouseId : true;
        const matchesStartDate = !filterStartDate || new Date(user.created_time || '') >= new Date(filterStartDate);
        const matchesEndDate = !filterEndDate || new Date(user.created_time || '') <= new Date(filterEndDate);
        const matchesYear = !filterYear || new Date(user.created_time || '').getFullYear().toString() === filterYear;
        return matchesSearch && matchesWarehouse && matchesStartDate && matchesEndDate && matchesYear;
    });
    const total = filteredUsers.length;
    const pageUsers = filteredUsers.slice((currentPage - 1) * perPage, currentPage * perPage);

    const handleOpenDeleteDialog = (userId: string) => {
        setSelectedUserId(userId);
        setIsOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedUserId) return;

        setIsDeleting(true);
        try {
            await deleteUser(selectedUserId);
            fetchAllUsers();
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete user:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>User Location Tracking</Heading>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={() => navigate('/gp/app/users/add')}>Create new app user</Button>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search app user…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />
            <ul>
                {loading && <div className="w-full text-center h-full py-36 justify-center"><Loading /></div>}
                {error && <p className="py-16 text-center text-red-700">{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    {isColumnVisible(columnVisibility, 'username') && <TableHeader>Username</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'email') && <TableHeader>Email</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'warehouse') && <TableHeader>Warehouse</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pageUsers.length > 0 ? [...pageUsers].sort((a, b) => {
                                    const cmp = (a.username || '').localeCompare(b.username || '');
                                    return sortOrder === 'az' ? cmp : -cmp;
                                }).map((user, index) => (
                                    <TableRow key={user.id}>
                                        <TableCell>{(currentPage - 1) * perPage + index + 1}</TableCell>
                                        {isColumnVisible(columnVisibility, 'username') && <TableCell>{user.username}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'email') && <TableCell>{user.email}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'warehouse') && <TableCell>{warehouses?.find(w => w.id?.toString() === user.warehouse_id?.toString())?.warehouse_name || '—'}</TableCell>}
                                        <TableCell className="py-5 flex gap-4 text-zinc-500 justify-center">
                                            <a onClick={() => navigate(`/gp/app/track/usercurrentlocation/view/${user.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8">No app user found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(total / perPage)}
                            onPageChange={setCurrentPage}
                            totalResult={total}
                            dataName={'Users'}
                        />
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                <div>
                    <Text>By Warehouse</Text>
                    <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                        <option value="">All Warehouses</option>
                        {warehouses?.map((warehouse) => (
                            <option key={warehouse.id} value={warehouse.id?.toString()}>
                                {warehouse.warehouse_name}
                            </option>
                        ))}
                    </Select>
                </div>
            </FiltersDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete App User</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this app user? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AppUserCurrentLocationPage;